/**
 * Tracks whether a user has already been dropped straight onto a board on
 * arrival, so that only their FIRST login lands there and every later one shows
 * the board library as before.
 *
 * Keyed by userId, so a different user signing in on the same browser still gets
 * their own one-shot landing.
 *
 * Deliberately localStorage-only, unlike the onboarding-completion flag
 * which needed a durable server record. The failure mode here is benign: a user
 * who clears their cache lands on their most recent board once more, which is a
 * mild convenience rather than a re-run of a flow they already finished. Promote
 * it to a server record only if that turns out to matter.
 */
const STORAGE_PREFIX = 'intuigence:first_board_landing:'

/** True if this user has already had their one-time landing on a board. */
export function hasLandedOnFirstBoard(userId?: string | null): boolean {
  if (!userId) return false
  try {
    return localStorage.getItem(STORAGE_PREFIX + userId) === 'true'
  } catch {
    // Private mode / storage disabled — treat as "already landed" so a user
    // whose choice can never be remembered isn't redirected on every login.
    return true
  }
}

/** Record that this user has had their one-time landing. */
export function markLandedOnFirstBoard(userId?: string | null): void {
  if (!userId) return
  try {
    localStorage.setItem(STORAGE_PREFIX + userId, 'true')
  } catch {
    // Ignore quota / disabled-storage errors; the landing is non-critical.
  }
}
